import React, { useState, useCallback, useEffect } from 'react';
import { Tabs, Modal, Input, Spin, message } from 'antd';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useModulePermission } from '../../hooks/usePermission';
import './D211Form.css';

import { useD211Mode } from './hooks/useD211Mode';
import { useD211Record } from './hooks/useD211Record';
import { useD211Navigation } from './hooks/useD211Navigation';
import { useLookupNames } from './hooks/useLookupNames';
import { useD211Approval } from './hooks/useD211Approval';
import { SharedActionBar } from '../../components/SharedActionBar';
import ApprovalHistoryModal from './components/ApprovalHistoryModal';
import Tab1BasicInfo from './components/Tab1BasicInfo';
import Tab2Detail from './components/Tab2Detail';
import Tab3Remarks from './components/Tab3Remarks';
import { getStatusBadge, isLockedForEdit } from './helpers';
import type { FD110, FD111 } from '../../services/d211Api';

// ====================================================================
// Empty master template (BR-011 defaults)
// ====================================================================
const EMPTY_MASTER: Partial<FD110> = {
  fd110_01: '', fd110_11: new Date().toISOString(), fd110_12: '',
  fd110_19: 'NTD', fd110_20: 1, fd110_28: '', fd110_29: 'N',
  fd110_30: '', fd110_31: '', fd110_34: '', fd110_40: 2,
  fd110_46: '2', fd110_55: true, fd110_80: '', fd110_53: '1', // 統一狀態碼：''=草稿
};

// ====================================================================
// D211Form — 計劃採購單 (Planned Purchase Order)
// ====================================================================
const D211Form: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const reviewId = searchParams.get('review');
  const permissions = useModulePermission('pwd211f1');
  const { mode, toBrowse, toAdd, toEdit, isEditing } = useD211Mode();
  const { record, loading, saving, load, save, remove } = useD211Record();
  const nav = useD211Navigation();
  const { names, resolveAllNames, resolveSingleName, clearNames } = useLookupNames();

  const [formData, setFormData] = useState<Partial<FD110>>({ ...EMPTY_MASTER });
  const [details, setDetails] = useState<Partial<FD111>[]>([]);
  const [selectedIdx, setSelectedIdx] = useState(-1);
  const [activeTab, setActiveTab] = useState('1');
  const [searchVisible, setSearchVisible] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState('');

  // ----------------------------------------------------------------
  // Load record helper (resolves names too)
  // ----------------------------------------------------------------
  const loadRecordWithNames = useCallback(async (id: string) => {
    const rec = await load(id);
    if (rec) {
      setFormData({ ...rec });
      setDetails(rec.details ? rec.details.map(d => ({ ...d })) : []);
      setSelectedIdx(rec.details && rec.details.length > 0 ? 0 : -1);
      await resolveAllNames(rec);
      nav.setCurrentByKey(id);
    }
    return rec;
  }, [load, resolveAllNames, nav]);

  // Approval hook
  const approval = useD211Approval({ record, isEditing, loadRecord: loadRecordWithNames });

  // ----------------------------------------------------------------
  // Initial load
  // ----------------------------------------------------------------
  useEffect(() => {
    const init = async () => {
      const ids = await nav.fetchList();
      if (reviewId) {
        await loadRecordWithNames(reviewId);
      } else if (ids.length > 0) {
        await loadRecordWithNames(ids[0]);
      }
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ----------------------------------------------------------------
  // Sync record to form when record changes externally
  // ----------------------------------------------------------------
  useEffect(() => {
    if (record) {
      setFormData({ ...record });
      setDetails(record.details ? record.details.map(d => ({ ...d })) : []);
    }
  }, [record]);

  // ----------------------------------------------------------------
  // Navigation handlers
  // ----------------------------------------------------------------
  const handleNav = useCallback(async (fn: () => string | null) => {
    const id = fn();
    if (id) await loadRecordWithNames(id);
  }, [loadRecordWithNames]);

  // ----------------------------------------------------------------
  // Field setters
  // ----------------------------------------------------------------
  const setField = useCallback((key: string, value: unknown) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  }, []);

  const onDetailChange = useCallback((idx: number, key: string, value: unknown) => {
    setDetails(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], [key]: value };
      return next;
    });
  }, []);

  // ----------------------------------------------------------------
  // CRUD handlers
  // ----------------------------------------------------------------
  const handleAdd = useCallback(() => {
    setFormData({ ...EMPTY_MASTER });
    setDetails([]);
    setSelectedIdx(-1);
    clearNames();
    toAdd();
    setActiveTab('1');
  }, [toAdd, clearNames]);

  const handleEdit = useCallback(() => {
    if (!record || isLockedForEdit(record)) return;
    toEdit();
  }, [record, toEdit]);

  const handleRevert = useCallback(() => {
    if (record) {
      setFormData({ ...record });
      setDetails(record.details ? record.details.map(d => ({ ...d })) : []);
      setSelectedIdx(record.details && record.details.length > 0 ? 0 : -1);
    } else {
      setFormData({ ...EMPTY_MASTER });
      setDetails([]);
      setSelectedIdx(-1);
    }
    toBrowse();
  }, [record, toBrowse]);

  const handleSave = useCallback(async () => {
    const isNew = mode === 'add';
    const result = await save(formData, details, isNew);
    if (result.success) {
      toBrowse();
      // Refresh the navigation list
      const ids = await nav.fetchList();
      if (record?.fd110_01 || formData.fd110_01) {
        nav.setCurrentByKey(record?.fd110_01 || formData.fd110_01 || '');
      } else if (ids.length > 0) {
        nav.setCurrentByKey(ids[ids.length - 1]);
      }
    }
  }, [mode, formData, details, save, toBrowse, nav, record]);

  const handleDelete = useCallback(() => {
    if (!record) return;
    Modal.confirm({
      title: '確認刪除',
      content: `是否刪除採購單 ${record.fd110_01}？`,
      okText: '刪除',
      okButtonProps: { danger: true },
      cancelText: '取消',
      onOk: async () => {
        const ok = await remove(record.fd110_01);
        if (ok) {
          const ids = await nav.fetchList();
          if (ids.length > 0) {
            await loadRecordWithNames(ids[0]);
          } else {
            setFormData({ ...EMPTY_MASTER });
            setDetails([]);
          }
          toBrowse();
        }
      },
    });
  }, [record, remove, nav, loadRecordWithNames, toBrowse]);

  // ----------------------------------------------------------------
  // Detail row management
  // ----------------------------------------------------------------
  const handleAddRow = useCallback(() => {
    const nextSeq = details.length + 1;
    const newRow: Partial<FD111> = {
      fd111_01: formData.fd110_01 || '',
      fd111_02: nextSeq,
      fd111_10: '', fd111_11: '', fd111_12: '',
      fd111_15: 0, fd111_16: '', fd111_17: 0, fd111_18: 0,
      fd111_25: 0, fd111_26: 0, fd111_29: 'N',
      fd111_30: 0, fd111_301: 0, fd111_302: 0,
      fd111_51: '', fd111_52: '', fd111_53: '', fd111_54: '',
      fd111_55: '', fd111_56: '',
    };
    setDetails(prev => [...prev, newRow]);
    setSelectedIdx(details.length);
  }, [details.length, formData.fd110_01]);

  const handleDeleteRow = useCallback((idx: number) => {
    if (idx < 0 || idx >= details.length) return;
    Modal.confirm({
      title: '確認刪除明細',
      content: `是否刪除第 ${idx + 1} 筆明細？`,
      okText: '刪除',
      okButtonProps: { danger: true },
      cancelText: '取消',
      onOk: () => {
        const next = details.filter((_, i) => i !== idx)
          .map((d, i) => ({ ...d, fd111_02: i + 1 })); // T-F05 reindex
        setDetails(next);
        setSelectedIdx(next.length > 0 ? Math.min(idx, next.length - 1) : -1);
      },
    });
  }, [details]);

  // ----------------------------------------------------------------
  // Search
  // ----------------------------------------------------------------
  const handleSearch = useCallback(() => { setSearchVisible(true); }, []);
  const handleSearchOk = useCallback(async () => {
    setSearchVisible(false);
    const ids = await nav.fetchList(searchKeyword || undefined);
    if (ids.length > 0) {
      await loadRecordWithNames(ids[0]);
    } else {
      message.info('查無符合條件的資料');
    }
    setSearchKeyword('');
  }, [searchKeyword, nav, loadRecordWithNames]);

  // ----------------------------------------------------------------
  // Status badge
  // ----------------------------------------------------------------
  const badge = getStatusBadge(record);
  const locked = isLockedForEdit(record);

  // ----------------------------------------------------------------
  // Render
  // ----------------------------------------------------------------
  return (
    <div className="d211-form">
      <Spin spinning={loading}>
        <div className="page-wrapper">
          {/* Top bar */}
          <div className="top-bar">
            <h3 style={{ margin: 0 }}>計劃採購單</h3>
            {badge && <span className={`status-badge ${badge.cls}`}>{badge.text}</span>}
            {record && <span style={{ fontSize: 12, color: '#999' }}>單號: {record.fd110_01}</span>}
          </div>

          {/* Tabs */}
          <Tabs activeKey={activeTab} onChange={setActiveTab} items={[
            {
              key: '1', label: '基本資料',
              children: <Tab1BasicInfo formData={formData} names={names}
                isEditing={isEditing} setField={setField}
                resolveSingleName={resolveSingleName} />,
            },
            {
              key: '2', label: '產品明細',
              children: <Tab2Detail details={details} selectedIdx={selectedIdx}
                isEditing={isEditing} isApproved={locked}
                onSelectRow={setSelectedIdx} onAddRow={handleAddRow}
                onDeleteRow={handleDeleteRow} onDetailChange={onDetailChange} />,
            },
            {
              key: '3', label: '嘜頭/備註/品質要求',
              children: <Tab3Remarks formData={formData} names={names}
                isEditing={isEditing} setField={setField}
                resolveSingleName={resolveSingleName} />,
            },
          ]} />
        </div>
      </Spin>

      {/* Action bar */}
      <SharedActionBar
        mode={mode} isEditing={isEditing} hasRecord={!!record}
        currentIdx={nav.currentIdx} recordCount={nav.list.length}
        onNavFirst={() => handleNav(nav.goFirst)}
        onNavPrev={() => handleNav(nav.goPrev)}
        onNavNext={() => handleNav(nav.goNext)}
        onNavLast={() => handleNav(nav.goLast)}
        onAdd={handleAdd} onModify={handleEdit}
        onDelete={handleDelete}
        onSave={handleSave} onCancel={handleRevert}
        approval={{
          statusCode: (record?.fd110_80 || '').trim(),
          isApproved: (record?.fd110_80 || '').trim() === 'P',
          isPending: ['D', 'S'].includes((record?.fd110_80 || '').trim()), // 統一碼 D + 舊碼 S
          isLocked: locked,
        }}
        onSubmit={approval.handleSubmit}
        onApprove={approval.handleApprove}
        onReject={approval.handleReject}
        onWriteoff={approval.handleWriteoff}
        onHistory={approval.handleViewHistory}
        onPrint={() => message.info('列印功能尚未實作')}
        onExit={() => navigate('/erp')}
        canCreate={permissions.canCreate} canEdit={permissions.canEdit}
        canDelete={permissions.canDelete} canApprove={permissions.canApprove}
        canPrint={permissions.canPrint}
      />

      {/* Search modal */}
      <Modal title="查詢採購單" open={searchVisible}
        onOk={handleSearchOk} onCancel={() => setSearchVisible(false)}
        okText="查詢" cancelText="取消">
        <Input placeholder="輸入採購單號或關鍵字" value={searchKeyword}
          onChange={e => setSearchKeyword(e.target.value)}
          onPressEnter={handleSearchOk} autoFocus />
      </Modal>

      {/* Approval History Modal */}
      {record && (
        <ApprovalHistoryModal
          visible={approval.historyVisible}
          orderNo={record.fd110_01}
          onClose={approval.closeHistory}
        />
      )}
    </div>
  );
};

export default D211Form;
